import type { Database } from '@/types/database.types';

type Task = Database['public']['Tables']['tasks']['Row'];
type TaskType = Database['public']['Enums']['task_type'];

/**
 * Swim lane type - Developer or QA
 */
export type SwimLaneType = 'developer' | 'qa';

/**
 * Grouped tasks by swim lane
 */
export interface SwimLaneGroups {
    developer: Task[];
    qa: Task[];
}

/**
 * Group tasks by swim lane based on task type.
 * Developer lane: Design, Development, Integration, Code Review
 * QA lane: QA
 */
export const groupTasksBySwimlane = (tasks: Task[]): SwimLaneGroups => {
    const developerTypes: TaskType[] = ['design', 'development', 'integration', 'code_review'];

    return {
        developer: tasks.filter(task => developerTypes.includes(task.type)),
        qa: tasks.filter(task => task.type === 'qa')
    };
};

/**
 * Get swim lane type for a given task type
 */
export const getSwimlaneType = (taskType: TaskType): SwimLaneType => {
    return taskType === 'qa' ? 'qa' : 'developer';
};

/**
 * Get swim lane label for display
 */
export const getSwimLaneLabel = (swimLaneType: SwimLaneType): string => {
    return swimLaneType === 'developer' ? 'Developer Tasks' : 'QA Tasks';
};

/**
 * Get swim lane background color as a Tailwind class
 */
export const getSwimLaneBackgroundColor = (swimLaneType: SwimLaneType): string => {
    return swimLaneType === 'developer' ? 'bg-blue-50' : 'bg-pink-50';
};

/**
 * Get swim lane background color as a hex value for D3 SVG fills.
 * Single source of truth for lane hex colors; matches getSwimLaneBackgroundColor.
 */
export const getLaneHexColor = (swimLaneType: SwimLaneType): string => {
    return swimLaneType === 'developer' ? '#EFF6FF' : '#FDF2F8'; // blue-50 / pink-50
};

/**
 * Calculate swim lane height using greedy first-fit row packing —
 * the same algorithm used by the component for task Y-position assignment.
 * This ensures the pre-allocated height always matches the actual rendered rows.
 *
 * Minimum height: 80px. Skips tasks with null or invalid dates.
 */
export const calculateSwimLaneHeight = (
    tasks: Task[],
    barHeight: number = 32,
    barPadding: number = 8
): number => {
    const minHeight = 80;
    if (tasks.length === 0) return minHeight;

    const rowCount = countGreedyPackingRows(tasks);
    const neededHeight = rowCount * (barHeight + barPadding) + 20;
    return Math.max(minHeight, neededHeight);
};

/**
 * Count the number of rows required by greedy first-fit interval packing.
 * Skips tasks whose start_date or end_date is null, invalid, or inverted.
 */
const countGreedyPackingRows = (tasks: Task[]): number => {
    const validTasks = tasks.filter(t => {
        if (t.start_date == null || t.end_date == null) return false;
        const s = new Date(t.start_date).getTime();
        const e = new Date(t.end_date).getTime();
        return !isNaN(s) && !isNaN(e) && s <= e;
    });

    if (validTasks.length === 0) return 1;

    const rows: Task[][] = [[]];

    validTasks.forEach(t => {
        const tStart = new Date(t.start_date).getTime();
        const tEnd = new Date(t.end_date).getTime();
        let placed = false;

        for (let r = 0; r < rows.length; r++) {
            const row = rows[r]!;
            const canFit = row.every(existing => {
                const eStart = new Date(existing.start_date).getTime();
                const eEnd = new Date(existing.end_date).getTime();
                return tEnd <= eStart || tStart >= eEnd;
            });
            if (canFit) {
                row.push(t);
                placed = true;
                break;
            }
        }

        if (!placed) {
            rows.push([t]);
        }
    });

    return rows.length;
};
